/**
 * Supabase clients — auth and realtime ONLY.
 *
 * We deliberately do NOT use `@supabase/supabase-js`. That package bundles the
 * PostgREST, Storage and Functions clients, and our architecture forbids all
 * three: every write goes through FastAPI so business rules, usage metering and
 * the audit log cannot be bypassed, and R2 is the storage layer.
 *
 * Importing the narrow packages instead removes ~30 KB gzipped of code we are
 * not allowed to call.
 *
 * See docs/01-architecture/02-tech-stack-decisions.md.
 */

import { GoTrueClient } from '@supabase/auth-js'
import type { RealtimeClient } from '@supabase/realtime-js'

const url = import.meta.env.VITE_SUPABASE_URL
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!url || !anonKey) {
  throw new Error(
    'VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY are required. ' +
      'Run ./start.sh, which reads them from `supabase status`.',
  )
}

const base = url.replace(/\/$/, '')

export const auth = new GoTrueClient({
  url: `${base}/auth/v1`,
  headers: {
    apikey: anonKey,
    Authorization: `Bearer ${anonKey}`,
  },
  autoRefreshToken: true,
  persistSession: true,
  detectSessionInUrl: true,
  flowType: 'pkce',
  storageKey: 'convo-auth',
})

/**
 * Realtime is loaded AND created lazily.
 *
 * The type-only import above plus this dynamic import keep ~17 KB gzipped of
 * WebSocket client out of the first paint. A static import would ship it to
 * every visitor including those who only ever open a settings page — the client
 * was already constructed lazily, but the CODE was not.
 */
let realtimeClient: RealtimeClient | null = null

export async function getRealtime(accessToken: string | null): Promise<RealtimeClient> {
  if (!realtimeClient) {
    const { RealtimeClient } = await import('@supabase/realtime-js')
    realtimeClient = new RealtimeClient(`${base.replace(/^http/, 'ws')}/realtime/v1`, {
      params: { apikey: anonKey, eventsPerSecond: 10 },
    })
  }
  // setAuth is async but callers do not need to await it: the token is applied
  // before the next heartbeat, and a stale token simply reconnects.
  if (accessToken) void realtimeClient.setAuth(accessToken)
  return realtimeClient
}

/** Called on sign-out and on workspace switch. */
export function disconnectRealtime(): void {
  void realtimeClient?.disconnect()
  realtimeClient = null
}
